import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { FileBarChart, Plus, RefreshCw, Download, Trash2, Pencil, Clock, Loader2, CheckCircle2, XCircle } from 'lucide-react'
import {
  useGeneratedReports,
  useGenerateReport,
  useDeleteReport,
  useDownloadReport,
  useReportSchedules,
  useCreateReportSchedule,
  useUpdateReportSchedule,
  useDeleteReportSchedule,
} from '@/hooks/data/useReports'
import type { GeneratedReport, ReportSchedule, ReportScheduleInput, ReportType } from '@/types/domain'
import EditModal from '@/components/crud/EditModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import { toast } from '@/ui/Toast'
import { userMessage } from '@/api/errors'

const TYPE_LABELS: Record<ReportType, string> = {
  Gst: 'GST Report',
  CampaignPerformance: 'Campaign Performance',
  DiscountUsage: 'Discount Usage',
}

function toDateInput(iso: string) {
  return iso ? iso.slice(0, 10) : ''
}
// Treats the typed YYYY-MM-DD as a UTC calendar date directly, rather than parsing it in the
// browser's local timezone first — `new Date('2026-06-01T00:00:00').toISOString()` shifts to
// the previous day for any timezone ahead of UTC (e.g. NZT), which silently mis-dates a report.
function startOfDayIso(dateLocal: string) {
  return `${dateLocal}T00:00:00.000Z`
}
function endOfDayIso(dateLocal: string) {
  return `${dateLocal}T23:59:59.999Z`
}

function StatusBadge({ status }: { status: GeneratedReport['status'] }) {
  const map = {
    Pending: { icon: Clock, cls: 'bg-gray-100 text-gray-600' },
    Running: { icon: Loader2, cls: 'bg-blue-100 text-blue-700' },
    Ready: { icon: CheckCircle2, cls: 'bg-green-100 text-green-700' },
    Failed: { icon: XCircle, cls: 'bg-red-100 text-red-700' },
  } as const
  const { icon: Icon, cls } = map[status]
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon className={`w-3 h-3 ${status === 'Running' ? 'animate-spin' : ''}`} />
      {status}
    </span>
  )
}

function AdminReportsPage() {
  const { data: reports = [], isLoading, isFetching, refetch } = useGeneratedReports()
  const generate = useGenerateReport()
  const deleteReport = useDeleteReport()
  const download = useDownloadReport()
  const confirmDelete = useDeleteConfirm()

  const { data: schedules = [] } = useReportSchedules()
  const createSchedule = useCreateReportSchedule()
  const updateSchedule = useUpdateReportSchedule()
  const deleteSchedule = useDeleteReportSchedule()

  const [generating, setGenerating] = useState(false)
  const [creatingSchedule, setCreatingSchedule] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<ReportSchedule | null>(null)

  const generateFields = [
    { key: 'type', label: 'Report', type: 'select' as const, required: true, options: Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label })) },
    { key: 'from', label: 'From', type: 'text' as const, required: true, placeholder: 'YYYY-MM-DD', hint: 'Date, e.g. 2026-07-01' },
    { key: 'to', label: 'To', type: 'text' as const, required: true, placeholder: 'YYYY-MM-DD' },
  ]

  const scheduleFields = [
    { key: 'type', label: 'Report', type: 'select' as const, required: true, options: Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label })) },
    {
      key: 'frequency', label: 'Frequency', type: 'select' as const, required: true,
      options: [{ value: 'Daily', label: 'Daily' }, { value: 'Weekly', label: 'Weekly' }, { value: 'Monthly', label: 'Monthly' }],
    },
    { key: 'recipientEmails', label: 'Recipient emails', type: 'textarea' as const, required: true, hint: 'Comma or newline separated' },
    { key: 'active', label: 'Active', type: 'checkbox' as const, placeholder: 'Run on schedule' },
  ]

  const parseEmails = (raw: string): string[] =>
    raw.split(/[,\n]/).map((e) => e.trim()).filter(Boolean)

  const scheduleToInput = (values: Record<string, any>): ReportScheduleInput => ({
    type: values.type,
    frequency: values.frequency,
    recipientEmails: parseEmails(values.recipientEmails ?? ''),
    active: !!values.active,
  })

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <FileBarChart className="w-6 h-6" /> Exports & Reports
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Real GST/Campaign Performance/Discount Usage PDFs — generated async (BillingService, TickerQ) from live OrderService/ProductService data. Standing schedules email a fresh copy on a cadence.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setGenerating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Export
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading exports…</p>
        ) : reports.length === 0 ? (
          <p className="p-6 text-gray-500">No exports yet — click "New Export" to generate one.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Report</th>
                <th className="px-4 py-2">Range</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Requested</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">
                    {TYPE_LABELS[r.type]}
                    {r.scheduled && <span className="ml-2 text-xs text-gray-400">(scheduled)</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{toDateInput(r.rangeFrom)} – {toDateInput(r.rangeTo)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={r.status} />
                    {r.status === 'Failed' && r.errorMessage && (
                      <div className="text-xs text-red-500 mt-1 max-w-xs truncate" title={r.errorMessage}>{r.errorMessage}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(r.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {r.downloadable && (
                        <button
                          onClick={async () => {
                            try {
                              await download.mutateAsync({ id: r.id, fileName: `${r.type}-${toDateInput(r.rangeFrom)}-${toDateInput(r.rangeTo)}.pdf` })
                            } catch (err) {
                              toast.error(userMessage(err))
                            }
                          }}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() =>
                          confirmDelete(`the ${TYPE_LABELS[r.type]} export`, async () => {
                            try {
                              await deleteReport.mutateAsync(r.id)
                            } catch (err) {
                              toast.error(userMessage(err))
                            }
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Scheduled Reports</h2>
        <button onClick={() => setCreatingSchedule(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-800">
          <Plus className="w-4 h-4" /> New Schedule
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {schedules.length === 0 ? (
          <p className="p-6 text-gray-500">No standing schedules — set one up to have a report emailed automatically.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Report</th>
                <th className="px-4 py-2">Frequency</th>
                <th className="px-4 py-2">Recipients</th>
                <th className="px-4 py-2">Next run</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((s) => (
                <tr key={s.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{TYPE_LABELS[s.type]}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{s.frequency}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 max-w-xs truncate" title={s.recipientEmails.join(', ')}>{s.recipientEmails.join(', ')}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(s.nextRunAt).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {s.active ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditingSchedule(s)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          confirmDelete(`the ${TYPE_LABELS[s.type]} schedule`, async () => {
                            try {
                              await deleteSchedule.mutateAsync(s.id)
                            } catch (err) {
                              toast.error(userMessage(err))
                            }
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <EditModal
        isOpen={generating}
        onClose={() => setGenerating(false)}
        title="New Export"
        fields={generateFields}
        initialValues={{ type: 'Gst' }}
        submitLabel="Generate"
        onSubmit={async (values) => {
          try {
            await generate.mutateAsync({ type: values.type, from: startOfDayIso(values.from), to: endOfDayIso(values.to) })
          } catch (err) {
            toast.error(userMessage(err))
            throw err
          }
        }}
      />

      <EditModal
        isOpen={creatingSchedule}
        onClose={() => setCreatingSchedule(false)}
        title="New Report Schedule"
        fields={scheduleFields}
        initialValues={{ type: 'Gst', frequency: 'Monthly', active: true }}
        submitLabel="Create"
        onSubmit={async (values) => {
          try {
            await createSchedule.mutateAsync(scheduleToInput(values))
          } catch (err) {
            toast.error(userMessage(err))
            throw err
          }
        }}
      />

      <EditModal
        isOpen={!!editingSchedule}
        onClose={() => setEditingSchedule(null)}
        title="Edit Report Schedule"
        fields={scheduleFields}
        initialValues={
          editingSchedule
            ? {
                type: editingSchedule.type,
                frequency: editingSchedule.frequency,
                recipientEmails: editingSchedule.recipientEmails.join(', '),
                active: editingSchedule.active,
              }
            : {}
        }
        onSubmit={async (values) => {
          if (!editingSchedule) return
          try {
            await updateSchedule.mutateAsync({ id: editingSchedule.id, body: scheduleToInput(values) })
          } catch (err) {
            toast.error(userMessage(err))
            throw err
          }
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/admin-reports/')({
  component: AdminReportsPage,
})
